import { PermissionError } from "libs/errors";
import { ADMIN_USER_ID } from "libs/shared-constants";
import { ActionServerContext } from "../action";
import { InvalidActionStateError } from "../utils/errors";
import { authorizeUserHasAccessToThread } from "./threadAuthorizers";

export async function authorizeUserHasAccessToMessage(props: { messageId: string; context: ActionServerContext }) {
  const { messageId, context } = props;

  const {
    environment: { loader },
  } = context;

  const [message] = await loader.getRecord("message", messageId);

  if (!message) {
    throw new PermissionError("User doesn't have access to message");
  }

  await authorizeUserHasAccessToThread({ threadId: message.thread_id, context });
}

export async function authorizeUserCanSendMessage(props: {
  draftId: string;
  branchedFrom?: {
    messageId: string;
    threadId: string;
  } | null;
  context: ActionServerContext;
}) {
  const { draftId, branchedFrom, context } = props;

  const {
    currentUserId,
    environment: { db, loader },
  } = context;

  // Fetch draft and assert read permissions for current user
  const [draft] = await loader.getRecord("draft", draftId);

  // Separately check that the current user can write to this draft
  if (draft?.user_id !== currentUserId) {
    throw new PermissionError(`draft:${draftId} doesn't belong to user`);
  }

  // Here we skip read authorization for the thread so that we can differentiate between a non-existent thread
  // and one that the exists but the user doesn't have permission to access.
  const [[thread], [branchedFromMessage]] = await Promise.all([
    db.getRecord("thread", draft.thread_id, { currentUserId: ADMIN_USER_ID }),
    branchedFrom ? loader.getRecord("message", branchedFrom.messageId) : [],
  ]);

  if (branchedFrom) {
    if (!branchedFromMessage) {
      throw new PermissionError(`User doesn't have access to branch parent message ${branchedFromMessage}`);
    } else if (branchedFrom.threadId !== branchedFromMessage.thread_id) {
      throw new InvalidActionStateError(
        `Thread being branched from (${branchedFrom.threadId}) does not match the thread of the message being branched from (${branchedFromMessage.thread_id})`,
      );
    }
  }

  // We do the following check because in the case where a user unsends a new thread draft, we don't delete the thread
  // but we do delete the permissions for it. And if the user sends the new thread draft again, the thread will exist,
  // but the permissions for it won't, so the authorizeUserHasAccessToThread check will fail.
  // Therefore, we'll just check to see if the thread was created by the user with the draft they're trying to send.
  const threadExistsAndBelongsToTheUser =
    thread &&
    thread.first_message_sender_user_id === currentUserId &&
    thread.first_message_id === draft.id &&
    thread.last_message_id === draft.id;

  await Promise.all([
    thread && !threadExistsAndBelongsToTheUser ?
      authorizeUserHasAccessToThread({
        threadId: thread.id,
        context,
      })
    : null,
    branchedFromMessage ?
      authorizeUserHasAccessToThread({
        threadId: branchedFromMessage.thread_id,
        context,
      })
    : null,
  ]);
}

// TODO: @Sudeep - This is ostensibly `authorizeUserHasAccessToMessage` with an extra param, consider just improving
//  `authorizeUserHasAccessToMessage` instead
export async function authorizeUserCanUpdateMessage(props: { messageId: string; context: ActionServerContext }) {
  const { messageId, context } = props;

  const {
    currentUserId,
    environment: { loader },
  } = context;

  const [message] = await loader.getRecord("message", messageId, { includeSoftDeletes: true });

  if (message?.sender_user_id !== currentUserId) {
    throw new PermissionError("User doesn't have access to message");
  }

  await authorizeUserHasAccessToThread({ threadId: message.thread_id, context });
}
